// index.ts
import { addDays, differenceInCalendarDays, isValid } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'

type Params = {
  customerTimeZone: string
  trialEndDate?: string | null
  now?: Date
  locale?: string
}

type Result = {
  actualTrialEndDate: Date
  differenceInDays: number
  displayDate: string
  hasTrialEnded: boolean
}

const convertRawTrialEndDate = ({
  customerTimeZone,
  trialEndDate,
  now = new Date(),
  locale,
}: Params): Result | null => {
  if (!trialEndDate) {
    return null
  }

  // 1. Parse the raw ISO string
  const parsed = new Date(trialEndDate)

  if (isValid(parsed) === false) {
    return null
  }

  // 2. Determine which locale to use for display
  const localeUsed = locale ?? 'en-US'

  // 3. Helper: get UTC Date corresponding to local midnight of a given Date in the customer's TZ
  const getStartOfLocalDay = (d: Date) => {
    // get the YYYY-MM-DD part in UTC
    const datePart = d.toISOString().split('T')[0]

    // interpret that as local midnight in customerTimeZone, return the equivalent UTC time
    return zonedTimeToUtc(datePart, customerTimeZone)
  }

  const trialLocalStart = getStartOfLocalDay(parsed)
  const nowLocalStart = getStartOfLocalDay(now)

  // 4. Compute how many days apart (−1, 0, 1, …)
  const differenceInDays = differenceInCalendarDays(trialLocalStart, nowLocalStart)

  // 5. The “actual” end‐of‐trial moment is local midnight + 1 day
  const actualTrialEndDate = addDays(trialLocalStart, 1)

  // 6. Format the display date in the customer’s TZ and the given locale
  const displayDate = trialLocalStart.toLocaleDateString(localeUsed, {
    dateStyle: 'medium',
    timeZone: customerTimeZone,
  })

  return {
    actualTrialEndDate,
    differenceInDays,
    displayDate,
    hasTrialEnded: differenceInDays < 0,
  }
}

export { convertRawTrialEndDate }
